import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createNamesTable, decompile } from '../../../../services/EpisodeScriptService';
import type {
  Fixture,
  NamesTable,
  SidecarMap3,
  SuppliedNames,
} from '../../../../dorian-shared/types/episodeScript';
import type { BasilDocument } from '../types/basilTypes';

type DecompiledDocument = {
  text: string;
  map: SidecarMap3 | null;
  names: NamesTable | null;
  errorMessage: string | null;
};

const EMPTY: DecompiledDocument = {
  text: '',
  map: null,
  names: null,
  errorMessage: null,
};

/**
 * The read half of the pipeline, memoised on the fixture (spec §4.5).
 *
 * `createNamesTable` then `decompile`, exactly as the prototype's server did, and then the text is
 * the textarea's value and nothing else holds a second copy of it. The decompiled text is kept as
 * the baseline the dirty check and `reset` compare against; `decompile`'s text ends with exactly
 * one newline, so the comparison is stable.
 *
 * `supplied` is the writer's own words for the book's things — today the memory bank's variables
 * (spec §10.1 K8) — not the `NamesTable`, which this hook builds from it.
 */
export function useBasilDocument(
  fixture: Fixture | null,
  supplied: SuppliedNames | null,
): BasilDocument {
  const decompiled = useMemo<DecompiledDocument>(() => {
    if (!fixture) {
      return EMPTY;
    }
    try {
      const names = createNamesTable(fixture, supplied ?? undefined);
      const result = decompile(fixture, { names: supplied ?? undefined });

      return {
        text: result.text,
        map: result.map,
        names: result.names ?? names,
        errorMessage: null,
      };
    } catch (error) {
      return {
        text: '',
        map: null,
        names: null,
        errorMessage: error instanceof Error ? error.message : "Basil couldn't open this episode. Try reloading it.",
      };
    }
  }, [fixture, supplied]);

  const [edited, setEdited] = useState<string | null>(null);

  // A fresh READ is a fresh document: the writer's edits belong to the episode they were made
  // against. It is bound to the fixture and NOT to the decompile, because `supplied` can change
  // under an open modal — the book's words for its characters, moods and locations are read beside
  // the episode and may land second (Basil v0.1.1) — and a re-decompile with better words is the
  // same episode, not a new one. Discarding a writer's typed text because a name arrived would be
  // losing their work over a cosmetic improvement; the sidecar map the compiler aligns on does not
  // depend on the words, so text typed against the id spellings still compiles exactly as it did.
  useEffect(() => {
    setEdited(null);
  }, [fixture]);

  const text = edited ?? decompiled.text;

  const setText = useCallback((next: string) => {
    setEdited(next);
  }, []);

  const reset = useCallback(() => {
    setEdited(null);
  }, []);

  return {
    text,
    // T17-04: the text the sidecar map's line numbers belong to, whatever the writer has typed over
    // it. The surface aligns the two rather than assuming they are still the same.
    baseline: decompiled.text,
    map: decompiled.map,
    names: decompiled.names,
    setText,
    reset,
    isDirty: edited != null && edited !== decompiled.text,
    errorMessage: decompiled.errorMessage,
  };
}
